import React, { useEffect, useMemo } from 'react';
import { Box, Typography, Grid, Card, CardMedia, CircularProgress, IconButton, TextField, InputAdornment, Chip } from '@mui/material';
import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAssets, setSearchQuery } from '../../redux/slices/assetsSlice';
import { addElement } from '../../redux/slices/canvasSlice';

/**
 * Image Modal - Shows images from AWS S3
 * Slide-in modal beside the left toolbar
 */
const ImageModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { assets, assetsLoading, searchQuery } = useSelector((state) => state.assets);

  useEffect(() => {
    // Always fetch fresh data when modal opens
    dispatch(fetchAssets());
  }, [dispatch]);

  const handleImageClick = (asset) => {
    dispatch(
      addElement({
        type: 'image',
        properties: {
          src: asset.url,
          objectFit: 'contain',
        },
        size: { width: 300, height: 200 },
      })
    );
    // Optionally close modal after adding
    // onClose();
  };

  // Filter and group assets by cuisine
  const { filteredAssets, groupedByCuisine } = useMemo(() => {
    const filtered = assets.filter(
      (asset) =>
        asset.type === 'image' &&
        (!searchQuery || 
          asset.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          asset.cuisineName?.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // Group by cuisine
    const grouped = {};
    filtered.forEach((asset) => {
      const cuisineName = asset.cuisineName || 'Other';
      if (!grouped[cuisineName]) {
        grouped[cuisineName] = [];
      }
      grouped[cuisineName].push(asset);
    });

    return { filteredAssets: filtered, groupedByCuisine: grouped };
  }, [assets, searchQuery]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          padding: 2,
          borderBottom: '1px solid #E0E0E0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Images
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <RiCloseLine size={20} />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ padding: 2, borderBottom: '1px solid #E0E0E0' }}>
        <TextField
          placeholder="Search images..."
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => dispatch(setSearchQuery(e.target.value))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <RiSearchLine size={18} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', padding: 2 }}>
        {assetsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAssets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center">
            No images found
          </Typography>
        ) : (
          <Box>
            {Object.entries(groupedByCuisine).map(([cuisineName, cuisineAssets]) => (
              <Box key={cuisineName} sx={{ marginBottom: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ marginRight: 1 }}>
                    {cuisineName}
                  </Typography>
                  <Chip 
                    label={cuisineAssets.length} 
                    size="small" 
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <Grid container spacing={2}>
                  {cuisineAssets.map((asset) => (
                    <Grid item xs={6} key={asset.id}>
                      <Card
                        onClick={() => handleImageClick(asset)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 3,
                          },
                          transition: 'all 0.2s',
                          height: '100%',
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="120"
                          image={asset.thumbnail || asset.url}
                          alt={asset.name}
                          sx={{ objectFit: 'contain' }}
                          onError={(e) => {
                            // Fallback if image fails to load
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <Box sx={{ padding: 0.5, textAlign: 'center' }}>
                          <Typography variant="caption" noWrap>
                            {asset.name}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ImageModal;

